package org.zq.astrock_ai.ui.research.gushi.data

import org.zq.astrock_ai.api.dto.QuantScanItemDto
import org.zq.astrock_ai.ui.research.gushi.model.HoldingPosition
import org.zq.astrock_ai.ui.research.gushi.model.StockItem
import org.zq.astrock_ai.util.formatDecimal

data class PortfolioRow(
    val code: String,
    val name: String,
    val costPrice: Double,
    val quantity: Int,
    val lastPrice: Double?,
    val marketValue: Double?,
    val costValue: Double,
    val pnl: Double?,
    val pnlPct: Double?,
)

data class PortfolioSummary(
    val rows: List<PortfolioRow>,
    val totalCost: Double,
    val totalMarket: Double?,
    val totalPnl: Double?,
    val totalPnlPct: Double?,
)

fun buildPortfolioSummary(
    holdings: Map<String, HoldingPosition>,
    stocks: List<StockItem>,
): PortfolioSummary {
    val priceByCode = stocks.associate { stock ->
        stock.code to stock.price.replace(",", "").toDoubleOrNull()
    }
    val rows = holdings.values
        .sortedBy { it.stockCode }
        .map { h ->
            val last = priceByCode[h.stockCode]
            val costValue = h.costPrice * h.quantity
            val market = last?.let { it * h.quantity }
            val pnl = market?.let { it - costValue }
            val pnlPct = if (last != null && h.costPrice > 0) {
                (last - h.costPrice) / h.costPrice * 100.0
            } else {
                null
            }
            PortfolioRow(
                code = h.stockCode,
                name = h.stockName,
                costPrice = h.costPrice,
                quantity = h.quantity,
                lastPrice = last,
                marketValue = market,
                costValue = costValue,
                pnl = pnl,
                pnlPct = pnlPct,
            )
        }
    val totalCost = rows.sumOf { it.costValue }
    val totalMarket = rows.mapNotNull { it.marketValue }.takeIf { it.size == rows.size }?.sum()
    val totalPnl = totalMarket?.let { it - totalCost }
    val totalPnlPct = if (totalCost > 0 && totalPnl != null) totalPnl / totalCost * 100.0 else null
    return PortfolioSummary(
        rows = rows,
        totalCost = totalCost,
        totalMarket = totalMarket,
        totalPnl = totalPnl,
        totalPnlPct = totalPnlPct,
    )
}

fun exportHoldingsText(summary: PortfolioSummary): String = buildString {
    appendLine("代码,名称,成本,数量,现价,市值,盈亏,盈亏%")
    summary.rows.forEach { r ->
        append(r.code).append(',')
        append(r.name.replace(',', ' ')).append(',')
        append(formatDecimal(r.costPrice)).append(',')
        append(r.quantity).append(',')
        append(r.lastPrice?.let { formatDecimal(it) } ?: "").append(',')
        append(r.marketValue?.let { formatDecimal(it) } ?: "").append(',')
        append(r.pnl?.let { formatDecimal(it) } ?: "").append(',')
        append(r.pnlPct?.let { formatDecimal(it) } ?: "")
        appendLine()
    }
    appendLine()
    append("合计成本,").append(formatDecimal(summary.totalCost)).appendLine()
    append("合计市值,").append(summary.totalMarket?.let { formatDecimal(it) } ?: "").appendLine()
    append("合计盈亏,").append(summary.totalPnl?.let { formatDecimal(it) } ?: "").appendLine()
    append("合计盈亏%,").append(summary.totalPnlPct?.let { formatDecimal(it) } ?: "").appendLine()
}

fun exportScanText(
    scanByCode: Map<String, QuantScanItemDto>,
    stocks: List<StockItem>,
    summaryLine: String?,
): String = buildString {
    if (!summaryLine.isNullOrBlank()) appendLine(summaryLine)
    appendLine("代码,名称,信号,买票,卖票,理由,现价,日期")
    val nameByCode = stocks.associate { it.code to it.name }
    scanByCode.values.sortedBy { it.code }.forEach { item ->
        append(item.code).append(',')
        append((item.name.ifBlank { nameByCode[item.code].orEmpty() }).replace(',', ' ')).append(',')
        append(item.signal).append(',')
        append(item.buyVotes).append(',')
        append(item.sellVotes).append(',')
        append(item.reason.replace(',', ' ').replace('\n', ' ')).append(',')
        append(if (item.price > 0) formatDecimal(item.price) else "").append(',')
        append(item.asOf.take(10))
        appendLine()
    }
}
